import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {LocationInListDTO, LocationTableRequest, LocationTableResponse} from "../../../api/generated";
import {locationApi, RequestStatus} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";

export const LOCATIONS_TABLE_ITEMS_ON_PAGE = 10

export type LocationTableRequestPartial = Partial<LocationTableRequest>

export const defaultLocationTableRequest: LocationTableRequest = {
    searchQuery: "",
    vehicleDataProviders: [],
    pageNumber: 0,
    pageSize: LOCATIONS_TABLE_ITEMS_ON_PAGE
}

export interface LocationState {
    totalCount: number
    locationList: LocationInListDTO[]
    locationListRequestStatus: RequestStatus
    locationTableRequest: LocationTableRequest
}

const buildInitialState = (): LocationState => {
    return {
        totalCount: 0,
        locationList: [],
        locationListRequestStatus: RequestStatus.NONE,
        locationTableRequest: defaultLocationTableRequest
    }
}

const initialState: LocationState = buildInitialState()

export const fetchLocationList = createAsyncThunk<LocationTableResponse, undefined, { state: RootState }>(
    "locationList/getLocationList",
    (_payload, {getState}) => {
        const locationTableRequest = getState().locationList.locationTableRequest
        return locationApi.getLocationsTable({
            locationTableRequest
        });
    }
);

export const deleteLocation = createAsyncThunk<string, string>(
    "locationList/deleteLocation",
    (publicId) => {
        return locationApi.deleteByPublicLocationId({publicId}).then(r => publicId);
    }
);

export const locationListSlice = createSlice({
    name: 'locationList',
    initialState,
    reducers: {
        clearLocationListState: () => {
            return buildInitialState();
        },
        updateLocationTableRequest: (state, {payload}: PayloadAction<LocationTableRequestPartial>) => {
            if (!payload.pageNumber) {
                payload.pageNumber = 0
            }
            state.locationTableRequest = {
                ...state.locationTableRequest,
                ...payload
            }
        },
        resetLocationTableRequest: (state) => {
            state.locationTableRequest = {
                ...defaultLocationTableRequest,
                sortBy: state.locationTableRequest.sortBy,
                sortDirection: state.locationTableRequest.sortDirection
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchLocationList.pending, (state) => {
            state.locationListRequestStatus = RequestStatus.PENDING;
        })
        builder.addCase(fetchLocationList.fulfilled, (state, {payload}) => {
            state.locationList = payload.locations;
            state.totalCount = payload.totalCount;
            state.locationListRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(fetchLocationList.rejected, (state) => {
            state.locationListRequestStatus = RequestStatus.REJECTED;
        })

        builder.addCase(deleteLocation.fulfilled, (state, {payload}) => {
            const publicId = payload
            const index = state.locationList.findIndex(location => location.publicId === publicId)
            state.locationList.splice(index, 1)
            state.totalCount = state.totalCount - 1
        })
    },
});

export const prepareLocationNameWithId = (name: string, id: string) => {
    return `${name}#${id.substring(id.length - 4)}`
}

export const {
    clearLocationListState,
    updateLocationTableRequest,
    resetLocationTableRequest
} = locationListSlice.actions;

export const locationListSelector = (state: RootState) => state.locationList.locationList;
export const locationListTotalCountSelector = (state: RootState): number => state.locationList.totalCount;
export const locationListRequestStatusSelector = (state: RootState): RequestStatus => state.locationList.locationListRequestStatus;
export const locationTableRequestSelector = (state: RootState): LocationTableRequest => state.locationList.locationTableRequest;
